import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableHead,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { React, useState } from "react";
import Delete from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  useDeleteFeeMutation,
  useGetFeesQuery,
  useUpdateFeeMutation,
} from "./feeScheduleApiSlice";
import { useGetSessionsPaginatedQuery } from "../settings/session/sessionApiSlice";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";
import toast from "../../../helpers/toast";

function FeesScheduleDialog({ open, setOpen, selectedClass }) {
  const [openWarning, setOpenWarning] = useState(false);
  const [focusedItem, setFocusedItem] = useState(null);
  const [editingFeeId, setEditingFeeId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const { data: fees, isLoading: getFeesLoading } = useGetFeesQuery(
    `classroom/${selectedClass?.id}`,
    { skip: !selectedClass?.id }
  );

  const { data: sessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 100 });
  const termNameOptions = Array.from(
    new Set(
      (sessionsData?.sessions ?? [])
        .flatMap((session) => session.terms ?? [])
        .map((term) => term.name)
        .filter(Boolean)
    )
  );

  const [deleteFee, { isLoading: deleteFeeLoading }] = useDeleteFeeMutation();
  const [updateFee, { isLoading: updateFeeLoading }] = useUpdateFeeMutation();

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = async () => {
    try {
      const response = await deleteFee(focusedItem);
      if (response.data) {
        toast.success("Fee deleted successfully");
      } else {
        toast.error("Error deleting Fee");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const startEditing = (fee) => {
    setEditingFeeId(fee.id);
    setEditValues({
      name: fee.name,
      amount: fee.amount,
      term_name: fee.term_name || "",
      gender: fee.gender || "",
      is_compulsory: fee.is_compulsory,
    });
  };

  const cancelEditing = () => {
    setEditingFeeId(null);
    setEditValues({});
  };

  const saveEditing = async (fee) => {
    try {
      await updateFee({
        fee_id: fee.id,
        name: editValues.name,
        amount: editValues.amount,
        classroom_id: fee.classroom_id,
        is_compulsory: editValues.is_compulsory,
        term_name: editValues.term_name || null,
        gender: editValues.gender || null,
      }).unwrap();
      toast.success("Fee updated successfully");
      cancelEditing();
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          minWidth: { xs: '95vw', sm: '70vw', md: '55vw' },
          minHeight: '75vh',
        },
      }}
    >
      <CustomWarningDialog
        open={openWarning}
        setOpen={setOpenWarning}
        item={focusedItem}
        action={handleDelete}
        message="Are you sure you want to delete this fee?"
      />
      <Box
        sx={{ display: "flex", padding: 2, justifyContent: "space-between" }}
      >
        <Typography>{selectedClass?.name}</Typography>
        <CloseSharpIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
      </Box>
      <Divider />

      <DialogContent>
        <Grid container columnSpacing={3}>
          {/* FIRST TERM */}
          <Grid item xs={12} md={12}>
            <StyledPaper>
              <Box sx={{ padding: "15px" }}>
                <Typography
                  variant="formSubHeading"
                  sx={{ textTransform: "none" }}
                >
                  {selectedClass?.name} Fees Schedule
                </Typography>
              </Box>
              {getFeesLoading && <LinearProgress />}
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "1.5px solid lightgrey " }}>
                      <StyledTableCell>NAME</StyledTableCell>
                      <StyledTableCell>AMOUNT</StyledTableCell>
                      <StyledTableCell>TERM</StyledTableCell>
                      <StyledTableCell>GENDER</StyledTableCell>
                      <StyledTableCell>TYPE</StyledTableCell>
                      <StyledTableCell>ACTIONS</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fees?.map((fee) =>
                      editingFeeId === fee.id ? (
                        <StyledTableRow key={fee.id}>
                          <StyledTableCell>
                            <TextField
                              size="small"
                              value={editValues.name}
                              onChange={(e) =>
                                setEditValues((v) => ({ ...v, name: e.target.value }))
                              }
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={editValues.amount}
                              onChange={(e) =>
                                setEditValues((v) => ({ ...v, amount: e.target.value }))
                              }
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Select
                              size="small"
                              displayEmpty
                              value={editValues.term_name}
                              onChange={(e) =>
                                setEditValues((v) => ({ ...v, term_name: e.target.value }))
                              }
                            >
                              <MenuItem value="">All Terms</MenuItem>
                              {termNameOptions.map((name) => (
                                <MenuItem key={name} value={name}>{name}</MenuItem>
                              ))}
                            </Select>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Select
                              size="small"
                              displayEmpty
                              value={editValues.gender}
                              onChange={(e) =>
                                setEditValues((v) => ({ ...v, gender: e.target.value }))
                              }
                            >
                              <MenuItem value="">All</MenuItem>
                              <MenuItem value="Male">Male</MenuItem>
                              <MenuItem value="Female">Female</MenuItem>
                            </Select>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Select
                              size="small"
                              value={editValues.is_compulsory}
                              onChange={(e) =>
                                setEditValues((v) => ({ ...v, is_compulsory: e.target.value }))
                              }
                            >
                              <MenuItem value={true}>Compulsory</MenuItem>
                              <MenuItem value={false}>Optional</MenuItem>
                            </Select>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: "flex", gap: 1 }} className="tableCellLink">
                              {updateFeeLoading ? (
                                <CircularProgress size={20} />
                              ) : (
                                <SaveIcon onClick={() => saveEditing(fee)} />
                              )}
                              <CloseIcon onClick={cancelEditing} />
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      ) : (
                        <StyledTableRow key={fee.id}>
                          <StyledTableCell
                            sx={{
                              color: "text.primary",
                              "&:hover": { cursor: "pointer" },
                            }}
                          >
                            {fee.name}
                          </StyledTableCell>

                          <StyledTableCell
                            sx={{
                              color: "text.primary",
                              "&:hover": { cursor: "pointer" },
                            }}
                          >
                            {fee.amount}
                          </StyledTableCell>

                          <StyledTableCell>
                            {fee.term_name || "Any term"}
                          </StyledTableCell>

                          <StyledTableCell>
                            {fee.gender || "All"}
                          </StyledTableCell>

                          <StyledTableCell
                            sx={{
                              color: "text.primary",
                              "&:hover": { cursor: "pointer" },
                            }}
                          >
                            {fee.is_compulsory ? "Compulsory" : "Optional"}
                          </StyledTableCell>

                          <StyledTableCell>
                            <Box sx={{ display: "flex", gap: 1 }} className="tableCellLink">
                              <EditIcon onClick={() => startEditing(fee)} />
                              {focusedItem?.id === fee.id && deleteFeeLoading ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Delete
                                  onClick={() => {
                                    setOpenWarning(true);
                                    setFocusedItem(fee);
                                  }}
                                />
                              )}
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>
          </Grid>
          {/* SECOND TERM */}
          {/* <Grid item xs={12} md={12}>
            <StyledPaper>
              <Box sx={{ padding: "15px" }}>
                <Typography variant="formHeading">Second Term</Typography>
                <Typography
                  variant="formSubHeading"
                  sx={{ textTransform: "none" }}
                >
                  {selectedClass.class} Second Term Fees Schedule
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "1.5px solid lightgrey " }}>
                      <StyledTableCell>ITEM</StyledTableCell>
                      <StyledTableCell>AMOUNT</StyledTableCell>
                      <StyledTableCell>TYPE</StyledTableCell>
                      <StyledTableCell>DELETE</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <StyledTableRow>
                      <StyledTableCell
                        sx={{
                          color: "primary.main",
                          "&:hover": { cursor: "pointer" },
                        }}
                      >
                        {(() => {
                          if (selectedClass.term === "Second Term") {
                            return selectedClass.item;
                          } else {
                            return "";
                          }
                        })()}
                      </StyledTableCell>

                      <StyledTableCell
                        sx={{
                          color: "primary.main",
                          "&:hover": { cursor: "pointer" },
                        }}
                      >
                        {(() => {
                          if (selectedClass.term === "Second Term") {
                            return selectedClass.amount;
                          } else {
                            return "";
                          }
                        })()}
                      </StyledTableCell>

                      <StyledTableCell
                        sx={{
                          color: "primary.main",
                          "&:hover": { cursor: "pointer" },
                        }}
                      >
                        {(() => {
                          if (selectedClass.term === "Second Term") {
                            return selectedClass.compulsory;
                          } else {
                            return "";
                          }
                        })()}
                      </StyledTableCell>

                      <StyledTableCell
                        sx={{
                          color: "primary.main",
                          "&:hover": { cursor: "pointer" },
                        }}
                      >
                        {(() => {
                          if (selectedClass.term === "Second Term") {
                            return <Delete />;
                          } else {
                            return "";
                          }
                        })()}
                      </StyledTableCell>
                    </StyledTableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>
          </Grid>
                      */}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export default FeesScheduleDialog;
